import {Button, Grid, Typography} from '@mui/material';
import React from 'react';
import {Box} from '@mui/system';
import {useSnackbar} from 'notistack';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import AutoField from '../../components/AutoForm/AutoFieldNew';
import {
  reportPackISAManagerField,
  reportPackSortCodeField,
  reportPackAccountField,
  ReportPackGenericFormType,
  reportPackFormType,
  isaManagerPostcodeField,
  isaManagerAddress1Field,
  isaManagerAddress2Field,
  isaTransferTypeField,
  isaManagerCityField,
  ReportPackGenericFormInnerType,
  reportPackIsaTransferTypeField,
  reportPackIsaTransferAmountField,
} from './ReportPackFields';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';

import {SaltusAny} from '../../utils/SaltusAny';
import {useReportPack} from './useReportPack';
import {getNumberOrNull} from '../../utils/getNumberOrNull';
import {getReportPackMissingFieldError} from './getReportPackMissingFieldError';
import {ReportPackFormFragment} from '../../schema-types/ReportPackFormFragment';

export const ReportPackISATransfer: React.FC<ReportPackGenericFormType> = ({
  formData,
  onClose,
  disabled,
}) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {onUpsertISATransferForm} = useReportPack();
  return (
    <ReportPackISATransferFormWrapped
      formFields={[]}
      disabled={disabled}
      submit={async (values) => {
        if (disabled) {
          return;
        }
        closeSnackbar();
        enqueueSnackbar(`Saving ${formData.formTypeLabel}...`, {
          variant: 'info',
        });
        await onUpsertISATransferForm({
          variables: {
            contactCuroId: values.contactCuroId,
            isaManagerAddress1: values.isaManagerAddress1,
            isaManagerAddress2: values.isaManagerAddress2,
            isaManagerName: values.isaManagerName,
            isaManagerPostcode: values.isaManagerPostcode,
            isaManagerSortCode: values.isaManagerSortCode,
            isaManagerCity: values.isaManagerCity,
            isaManagerPlanRef: values.isaManagerPlanRef,
            formTransferType: values.formTransferType as SaltusAny,
            reportPackId: String(formData.reportPackId),
            formId: formData.id,
            isaTransferAmount: getNumberOrNull(values.isaTransferAmount),
            isaTransferType: values.isaTransferType,
          },
        })
          .then(({data}) => {
            if (data?.upsertIsaTransferForm?.ok) {
              closeSnackbar();
              enqueueSnackbar(`Successfully saved ${formData.formTypeLabel}`, {
                variant: 'success',
              });
              onClose();
            } else {
              throw Error(data?.upsertIsaTransferForm?.errors?.join(','));
            }
          })
          .catch((err) => {
            closeSnackbar();
            enqueueSnackbar(`Could not save ${formData.formTypeLabel} ${err}`, {
              variant: 'error',
            });
          });
      }}
      initialValueOverrides={{
        ...formData,
        reportPackId: String(formData.reportPackId),
        contactCuroId: String(formData.contactCuroId),
      }}
    />
  );
};

const ReportPackISATransferForm: React.FC<ReportPackGenericFormInnerType> = ({
  disabled,
}) => {
  const {submitForm, isSubmitting, values} = useAutoForm<ReportPackFormFragment>();

  return (
    <Box maxWidth={500}>
      <Typography>Current provider details:</Typography>
      <Grid container p={2} mb={2} spacing={2} direction="column">
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, reportPackISAManagerField.id)}
            field={reportPackISAManagerField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, reportPackAccountField.id)}
            field={reportPackAccountField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, reportPackSortCodeField.id)}
            field={{...reportPackSortCodeField, label: 'Sort code (if cash ISA only)'}}
          />
        </Grid>
      </Grid>
      <Typography>Current provider address:</Typography>
      <Grid container p={2} mb={2} spacing={2} direction="column">
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, isaManagerAddress1Field.id)}
            field={isaManagerAddress1Field}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, isaManagerAddress2Field.id)}
            field={isaManagerAddress2Field}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, isaManagerCityField.id)}
            field={isaManagerCityField}
          />
        </Grid>
        <Grid item>
          <AutoField readOnly={disabled} field={isaManagerPostcodeField} />
        </Grid>
      </Grid>
      <Typography>Transfer type:</Typography>
      <Grid container p={2} mb={2} spacing={2} direction="column">
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, isaTransferTypeField.id)}
            field={isaTransferTypeField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackIsaTransferTypeField.id
            )}
            field={reportPackIsaTransferTypeField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackIsaTransferAmountField.id
            )}
            field={reportPackIsaTransferAmountField}
          />
        </Grid>
      </Grid>
      <Box position="absolute" bottom={20} right={20}>
        {!disabled && (
          <Button
            variant="contained"
            color="secondary"
            disabled={isSubmitting}
            onClick={() => submitForm()}
          >
            Save form
          </Button>
        )}
      </Box>
    </Box>
  );
};

const ReportPackISATransferFormWrapped = withAutoFormNew<
  reportPackFormType,
  ReportPackGenericFormInnerType
>(ReportPackISATransferForm, {}, {debugPosition: 'bottom'});
